import React, { useMemo, useCallback, useState, useEffect } from "react";
// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";
// Redux (to focus selected in the timeline)
import { ITableColumn } from "src/components/FilterSearchTable/TableComponents/TableHeader";
import { useDispatch } from "react-redux";
import { Grid, Button, Typography, Box } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
// Permissions imports
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector } from "react-redux";
import { hasPermission, IBidPackageResponse, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { IBidPackageResponseListItem, BidPackageStatus } from "@vmosolutions/oc-interfaces";
import EditRounded from "@mui/icons-material/EditRounded";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import _ from "lodash";
import {
  getBidPackageList,
  gotBidPackageList,
  setBidPackage as setLoadedBidPackage,
} from "src/redux/actions/Bidding.Actions";
import { getBidPackage, ICrewBid } from "src/pages/BidManager/helpers";
import CircularProgress from "@mui/material/CircularProgress";
import { setSelectedCrewDays, setSelectedCrewEvents } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { SelectField } from "src/components/FormikComponents/SelectField";

interface IBidPackageListProps {
  path: string[];
  id: string;
  windowProps?: object;
  // data?: IContent[];
}

export const addStatusToBidPackages = (packages: IBidPackageResponseListItem[]): IBidPackageResponseListItem[] => {
  if (packages?.length > 0) {
    const now = new Date();
    const updatedPackages = packages?.map((PackageItem) => {
      //const PackageItem = row as IBidPackageResponseListItem;
      const windowOpen = new Date(PackageItem?.bidWindowOpen);
      const windowClose = new Date(PackageItem?.bidWindowClose);
      const blockStart = new Date(PackageItem?.bidBlockStart);
      const blockEnd = new Date(PackageItem?.bidBlockEnd);
      let packageStatus: BidPackageStatus = undefined;
      switch (true) {
        case PackageItem.isDisabled:
          packageStatus = "DISABLED";
          break;
        case now > blockEnd:
          packageStatus = "Block Ended";
          break;
        case now >= blockStart && now <= blockEnd:
          packageStatus = "Block Active";
          break;
        case now > windowClose && now < blockStart:
          packageStatus = "Bidding Closed";
          break;
        case now >= windowOpen && now <= windowClose:
          packageStatus = "Bidding Open";
          break;
        case now < windowOpen:
          packageStatus = "Bidding Pending";
          break;
      }
      return { ...PackageItem, status: packageStatus };
    });
    return updatedPackages;
  }
  return packages;
};
/////////////////////////////////////////////////////
/////             STATIC TABLE DATA            //////
/////////////////////////////////////////////////////

// ASSIGNED FLIGHTS
const assignedFlightsTableDesc = {
  tableTitle: "Bid Packages",
  tableDescription: "",
  noMessage: "No bid packages to display, use the filter above to search.",
};
const assignedFlightsTableInfo: ITableColumn[] = [
  {
    id: "_id",
    value: "_id",
    index: 0,
    numeric: true,
    label: "id",
    type: "string",
    width: 1,
    visible: false,
    filter: false,
  },
  {
    id: "title",
    value: "title",
    index: 1,
    numeric: false,
    label: "Title",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "status",
    value: "status",
    index: 2,
    numeric: false,
    label: "Status",
    type: "string",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "positions",
    value: "positions",
    index: 3,
    numeric: false,
    label: "Assignment Positions",
    type: "array",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "crewGroups",
    value: "crewGroups",
    index: 4,
    numeric: false,
    label: "Crew Groups",
    type: "array",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidWindowOpen",
    value: "bidWindowOpen",
    index: 5,
    numeric: false,
    label: "Bid Window Open",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidWindowClose",
    value: "bidWindowClose",
    index: 6,
    numeric: false,
    label: "Bid Window Close",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidBlockStart",
    value: "bidBlockStart",
    index: 7,
    numeric: false,
    label: "Bid Block Start",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidBlockEnd",
    value: "bidBlockEnd",
    index: 8,
    numeric: false,
    label: "Bid Block End",
    type: "date",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidPairingCount",
    value: "bidPairingCount",
    index: 9,
    numeric: true,
    label: "Bid Pairing Count",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
  {
    id: "bidCount",
    value: "bidCount",
    index: 10,
    numeric: true,
    label: "Crew Bid Count",
    type: "number",
    width: 10,
    visible: true,
    filter: false,
  },
];

interface IStatusFilterOption {
  label: string;
  value: string;
  color: string | null;
}
const defaultFilter: IStatusFilterOption = { label: "Show All", value: "all", color: "white" };
const statusFilterOptions: IStatusFilterOption[] = [
  defaultFilter,
  { label: "Bidding Pending", value: "bidding_pending", color: null },
  { label: "Bidding Open", value: "bidding_open", color: "Plum" },
  { label: "Bidding Closed", value: "bidding_closed", color: "Plum" },
  { label: "Block Active", value: "block_active", color: "MediumTurquoise" },
  { label: "Block Ended", value: "block_ended", color: "MediumTurquoise" },
  { label: "DISABLED", value: "disabled", color: "orange" },
];

const filterBidPackageList = (
  packages: IBidPackageResponseListItem[],
  filter: IStatusFilterOption
): IBidPackageResponseListItem[] => {
  const now = new Date();
  switch (filter.value) {
    case "disabled":
      return packages?.filter((p) => p.isDisabled);
    case "block_ended":
      return packages?.filter((p) => now > new Date(p.bidBlockEnd) && !p.isDisabled);
    case "block_active":
      return packages?.filter(
        (p) => now >= new Date(p.bidBlockStart) && now <= new Date(p.bidBlockStart) && !p.isDisabled
      );
    case "bidding_closed":
      return packages?.filter(
        (p) => now > new Date(p.bidWindowClose) && now < new Date(p.bidBlockStart) && !p.isDisabled
      );
    case "bidding_open":
      return packages?.filter(
        (p) => now >= new Date(p.bidWindowOpen) && now <= new Date(p.bidWindowClose) && !p.isDisabled
      );
    case "bidding_pending":
      return packages?.filter((p) => now < new Date(p.bidWindowOpen) && !p.isDisabled);
    default:
      return packages; //filter((p) => !p.isDisabled);
  }
};

export const BidPackageListWindow = (props: IBidPackageListProps): JSX.Element => {
  const { id, path } = props;
  const dispatch = useDispatch();
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);

  // Permissions
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);
  const canCreateBidPackage = hasPermission(policies, PolicyResource.Bidding, PolicyAction.Create);
  const canUpdateBidPackage = hasPermission(policies, PolicyResource.Bidding, PolicyAction.Update);

  const [triedLoadingOnce, setTriedLoadingOnce] = useState<boolean>(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false);
  const [selectedBidPackage, setSelectedBidPackage] = useState<IBidPackageResponseListItem | null>(null);
  const loadedBidPackage = useSelector((state: IRootState) => state.bidding.selectedBidPackage);

  // Retrieve bid package list if not already present in redux.
  const bidPackages = useSelector((state: IRootState) => state.bidding.bidPackages);

  const getAllBidPackages = useCallback(async (): Promise<void> => {
    setIsLoadingPackages(true);
    setSelectedBidPackage(null);
    const response = await getBidPackageList();
    if (Array.isArray(response) && !!response?.length) {
      //const sortedList = response.sort((a, b) => new Date(a.bidWindowOpen) - new Date(b.bidWindowOpen));
      const bidPackageListWithStatus = addStatusToBidPackages(response);
      dispatch(gotBidPackageList(bidPackageListWithStatus));
    }
    setIsLoadingPackages(false);
  }, [dispatch]);

  // when component is mounted, attempt to load the bid packages once.
  useEffect(() => {
    if (bidPackages?.length === 0 && !triedLoadingOnce) {
      setTriedLoadingOnce(true);
      getAllBidPackages();
    }
  }, [bidPackages, getAllBidPackages, triedLoadingOnce]);

  const [statusFilter, setStatusFilter] = useState<IStatusFilterOption>(defaultFilter);

  //IBidPackageResponseListItem
  // Function used to create the dataSet needed for the table component.
  // This particular dataSet contains an array with all the available data types for the advanced search functionality.
  // use useMemo to prevent the whole table from re-rendering
  const tableData: any = useMemo(
    () => [
      {
        id: "bidPackages",
        dataType: "bidPackages",
        label: "Bid Packages",
        data: bidPackages?.length ? filterBidPackageList(bidPackages, statusFilter) : [],
        tableInfo: assignedFlightsTableInfo,
        tableInfoDisplay: assignedFlightsTableDesc,
      },
    ],
    [bidPackages, statusFilter]
  );

  // Return true if selected package is ready for assignment.
  const canAssignSelectedPackage: boolean = useMemo(() => {
    if (!selectedBidPackage || selectedBidPackage?.isDisabled) {
      return false;
    }
    const now = new Date();
    const biddingWindowClose = new Date(selectedBidPackage.bidWindowClose);
    const biddingBlockStart = new Date(selectedBidPackage.bidBlockStart);
    const bidCount = selectedBidPackage?.bidCount ?? 0;
    return bidCount && now > biddingWindowClose && now < biddingBlockStart;
  }, [selectedBidPackage]);

  const [isLoadingPackage, setIsLoadingPackage] = useState<boolean>(false);

  const openPairingAssignmentWindow = useCallback(async (): Promise<void> => {
    setIsLoadingPackage(true);
    const response: IBidPackageResponse = await getBidPackage(selectedBidPackage?._id);
    if (response?.bidPackage?._id) {
      // Clear any selected crew sched events to prepare window for filtered crew list.
      dispatch(setSelectedCrewEvents([], false, false, false));
      dispatch(setSelectedCrewDays(null));
      // Set Crew Shedule window into bidding mode.
      dispatch(setLoadedBidPackage(response));
    }
  }, [dispatch, selectedBidPackage]);

  useEffect(() => {
    // Only open the Crew Schedule window if the selected package has been assigned to redux and is ready to display.
    if (
      isLoadingPackage &&
      !!loadedBidPackage?.bidPackage?._id &&
      loadedBidPackage?.bidPackage?._id === selectedBidPackage?._id
    ) {
      dispatch(openWindow("Crew Schedule", "CrewScheduleWindow", [], {}, "content"));
      setIsLoadingPackage(false);
    }
  }, [dispatch, isLoadingPackage, loadedBidPackage, selectedBidPackage]);

  // Enable Bid Assignment button.
  const bidsToAssign = useMemo(
    () =>
      selectedBidPackage?._id && typeof selectedBidPackage?.bidCount === "number" && selectedBidPackage?.bidCount > 0,
    [selectedBidPackage?._id, selectedBidPackage?.bidCount]
  );

  // Create new bid package (or duplicate a selected one.)
  const createNewBidPackage = useCallback((): void => {
    dispatch(
      openWindow(
        "New Bid Package ",
        "BidPackageEditorWindow",
        [],
        { bidPackagelistItem: selectedBidPackage, duplicate: !!selectedBidPackage },
        "content"
      )
    );
  }, [dispatch, selectedBidPackage]);

  const editBidPackage = useCallback((): void => {
    dispatch(
      openWindow(
        "Edit Bid Package ",
        "BidPackageEditorWindow",
        [],
        { bidPackagelistItem: selectedBidPackage },
        "content"
      )
    );
  }, [dispatch, selectedBidPackage]);

  /**
   * SELECT + FOCUS? events selected in the menu
   * @param events - events to select in timeline
   * @param focusSelected - whether or not to focus on those events (zoom contain)
   * @param dataType - the type of event i.e. pairings, flights etc.
   */
  const handleSelectBidPackage = useCallback(
    (events: IBidPackageResponseListItem[], focusSelected?: boolean, dataType?: string): void => {
      switch (dataType) {
        case "bidPackages":
          setSelectedBidPackage(events?.length ? events[0] : null);
          break;
        default:
        // code block
      }
    },
    []
  );
  // console.log("selectedBidPackage", selectedBidPackage);

  return (
    <Box
      sx={{
        padding: "15px",
        width: "100%",
        "& .create_bidpackage_btn, & .update_bidpackage_btn, & .assign_bids_btn, & .refresh_bids_btn": {
          fontSize: 12,
          padding: "2px 8px",
          marginRight: "10px",
          "& svg": {
            width: "12px",
            height: "12px",
            marginRight: "5px",
          },
        },
      }}
    >
      <SpinnerProgress className="loadingSpinner" loading={isLoadingPackages} message="loading packages" />
      <FilterSearchTable
        id={id}
        path={path}
        className="bid_package_management"
        events={tableData}
        parentId={mosaicPanel}
        uniqueKey={"_id"}
        allowMultiFilter={true}
        hideFocusSelected={true}
        changeSelectedRows={handleSelectBidPackage}
        selectModeSingle={true}
        defaultOrderKey="bidWindowOpen" // Default Sort by bid window open
        // selectUsingCheckboxesOnly={true}
        showEventActions={true}
        defaultRowsPerPage={100}
        optionalControls={
          <Grid container style={{ width: "auto" }}>
            <Button
              id={"refresh_bids_btn"}
              variant="contained"
              color="primary"
              disabled={isLoadingPackages}
              onClick={_.debounce(getAllBidPackages, 500)}
              className="refresh_bids_btn"
            >
              {bidPackages?.length === 0 ? "Load Bid Packages" : "Refresh"}
            </Button>
            <Button
              id={"create_bidpackage_btn"}
              variant="outlined"
              disabled={!canCreateBidPackage}
              onClick={createNewBidPackage}
              className="create_bidpackage_btn"
            >
              <OpenInNewIcon />
              {selectedBidPackage ? "DUPLICATE SELECTED" : "CREATE NEW BID PACKAGE"}
            </Button>
            <Button
              id={"update_bidpackage_btn"}
              variant="outlined"
              disabled={!canUpdateBidPackage || !selectedBidPackage}
              onClick={editBidPackage}
              className="update_bidpackage_btn"
            >
              <EditRounded />
              EDIT SELECTED
            </Button>
            {selectedBidPackage && (
              <Button
                id={"assign_bids_btn"}
                variant="contained"
                color="primary"
                disabled={!canUpdateBidPackage || !bidsToAssign || !canAssignSelectedPackage}
                onClick={openPairingAssignmentWindow}
                className="assign_bids_btn"
              >
                {isLoadingPackage ? (
                  <CircularProgress style={{ color: "white", width: 14, height: 14, margin: 5 }} />
                ) : (
                  <OpenInNewIcon />
                )}
                {bidsToAssign ? "Assign Crew Bids" : "No Bids To Assign"}
              </Button>
            )}

            <Grid container alignItems="center" style={{ marginLeft: 15, width: "auto" }} wrap="nowrap">
              <Typography variant="caption">Filter By Package Status</Typography>
              <Grid item style={{ width: 300, marginLeft: 10 }}>
                <SelectField
                  className="field selectfield status_filter"
                  name="bid_package_status_filter"
                  label=""
                  isMulti={false}
                  formatOptionLabel={(option: IStatusFilterOption) => (
                    <Typography variant="caption" style={{ color: option.color }}>
                      {option.label}
                    </Typography>
                  )}
                  options={statusFilterOptions}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
              </Grid>
            </Grid>
          </Grid>
        }
      />
    </Box>
  );
};
